import { useState } from 'react'

import { personaThumbUrl } from '@/utils/personaThumbUrl'

/**
 * usePersonaThumbFallback — the one-shot thumbnail/original fallback pattern
 * used across every persona-portrait render site (docs/plans/portrait-thumbnails-adoption.md).
 *
 * Resolves `url` to its pre-generated `-t{size}.webp` variant. If that variant
 * 404s (not backfilled for this persona), it retries the original full-size
 * `url` exactly once; only after THAT fails does `handleImageError`'s
 * `onFinalError` run, letting the caller fall back to an initials avatar, a
 * static placeholder, or hide the image — whatever that surface already does.
 *
 * `thumbUrl === url` (host doesn't match the GCS pattern, so `personaThumbUrl`
 * passed it through unchanged) skips straight to `onFinalError` instead of a
 * pointless identical retry.
 */
export function usePersonaThumbFallback(url: string | null | undefined, size: 64 | 128 | 256) {
  const [thumbFailed, setThumbFailed] = useState(false)

  const resolvedUrl = url ?? undefined
  const thumbUrl = personaThumbUrl(resolvedUrl, size)
  const displayUrl = thumbFailed || !thumbUrl ? resolvedUrl : thumbUrl

  function handleImageError(onFinalError?: () => void) {
    if (!thumbFailed && thumbUrl && thumbUrl !== resolvedUrl) {
      setThumbFailed(true)
      return
    }
    onFinalError?.()
  }

  return { displayUrl, handleImageError }
}

export interface PersonaThumbImgProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'onError'> {
  src: string | null | undefined
  size: 64 | 128 | 256
  /** Called only after both the thumbnail AND the original url have failed. */
  onFinalError?: () => void
}

/**
 * PersonaThumbImg — drop-in `<img>` for the common case: no custom broken
 * state, just render nothing once both the thumbnail and the original url
 * have failed (or call `onFinalError` to swap in an initials avatar, etc.).
 *
 * Surfaces with pre-existing custom fallback state (e.g. an initials-avatar
 * component) should use `usePersonaThumbFallback` directly instead — see
 * `QuickStudyOpenEndCard`'s `QuoteAvatar` for that shape.
 */
export function PersonaThumbImg(props: PersonaThumbImgProps) {
  const { src, size, onFinalError, ...imgProps } = props
  const [hidden, setHidden] = useState(false)
  const { displayUrl, handleImageError } = usePersonaThumbFallback(src, size)

  if (!displayUrl || hidden) {
    return null
  }

  return (
    <img
      src={displayUrl}
      onError={() => handleImageError(() => {
        setHidden(true)
        onFinalError?.()
      })}
      {...imgProps}
    />
  )
}
